import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Divider,
  Avatar,
  Select,
  MenuItem,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SendIcon from '@mui/icons-material/Send';
import makeStyles from '@mui/styles/makeStyles';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import request from '../../../services/Request';
import Swal from 'sweetalert2';
import LoadingIcon from '../../items/buttons/LoadingIcon';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    borderRadius: 12,
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
        : '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  ticketCard: {
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
  },
  statusOpen: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  statusClosed: {
    backgroundColor: '#9e9e9e',
    color: 'white',
  },
  categoryChip: {
    marginRight: theme.spacing(1),
  },
  replyBox: {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
  },
  filePreview: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  previewItem: {
    position: 'relative',
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #ddd',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  removeButton: {
    position: 'absolute',
    top: 2,
    right: 2,
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: 'white',
    padding: 2,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.7)',
    },
  },
}));

export default function TicketContent() {
  const classes = useStyles();
  const user = JSON.parse(localStorage.getItem('user'));
  const [tickets, setTickets] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [mediaPreview, setMediaPreview] = useState({ open: false, url: '', type: '' });

  // Create ticket form state
  const [formData, setFormData] = useState({
    category: 'Software',
    type: 'Issue',
    title: '',
    description: '',
    machineId: '',
  });
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);

  // Tech user specific states for ticket creation
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isInternal, setIsInternal] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [priority, setPriority] = useState('medium');
  const [priorityLoading, setPriorityLoading] = useState(false);

  // Priority options
  const priorityOptions = [
    { value: 'low', label: 'Low', color: '#4caf50' },
    { value: 'medium', label: 'Medium', color: '#2196f3' },
    { value: 'high', label: 'High', color: '#ff9800' },
    { value: 'urgent', label: 'Urgent', color: '#f44336' },
  ];

  // Admin, TicketUser, and Tech use their own email as referTo (they are their own group)
  const referTo = (user?.role === 'Admin' || user?.role === 'TicketUser' || user?.role === 'Tech')
    ? user?.email
    : user?.referTo;
  const isTechUser = user?.role === 'Tech';
  const techResponsibilities = user?.techResponsibilities;

  // Get default date 30 days ago for Tech users
  const getDefault30DaysAgo = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };

  // Get today's date
  const getToday = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Filter states - Tech users default to 'open' status (for backend query)
  const [filterGroupname, setFilterGroupname] = useState('');
  const [filterStatus, setFilterStatus] = useState(isTechUser ? 'open' : 'all');
  const [filterDateFrom, setFilterDateFrom] = useState(isTechUser ? getDefault30DaysAgo() : '');
  const [filterDateTo, setFilterDateTo] = useState(isTechUser ? getToday() : '');
  const [filterKeyword, setFilterKeyword] = useState('');

  // Local filter and sort states (no API call needed)
  const [localCategoryFilter, setLocalCategoryFilter] = useState('all');
  const [localStatusFilter, setLocalStatusFilter] = useState('all');
  const [localKeyword, setLocalKeyword] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, priority-high, priority-low

  useEffect(() => {
    // Initial fetch with default filters
    if (isTechUser) {
      fetchTickets({
        fromDate: getDefault30DaysAgo(),
        toDate: getToday(),
        status: 'open',  // Default to open tickets for Tech users
        groupname: '',
      });
      // Also fetch companies for group filter
      fetchCompanies();
    } else {
      fetchTickets();
    }
  }, []);

  const fetchTickets = async (filters = {}) => {
    try {
      let response;

      if (isTechUser && techResponsibilities) {
        // Tech users get ALL tickets filtered by their categories
        const categories = [];
        if (techResponsibilities.hardware) categories.push('Hardware');
        if (techResponsibilities.software) categories.push('Software');

        if (categories.length === 0) {
          setTickets([]);
          return;
        }

        // Build query params
        const params = new URLSearchParams();
        params.append('categories', categories.join(','));

        if (filters.fromDate) params.append('fromDate', filters.fromDate);
        if (filters.toDate) params.append('toDate', filters.toDate);
        if (filters.status && filters.status !== 'all') params.append('status', filters.status);
        if (filters.groupname) params.append('groupname', filters.groupname);
        if (filters.keyword && filters.keyword.trim()) params.append('keyword', filters.keyword.trim());

        response = await request({
          method: 'get',
          url: `/tickets-by-category?${params.toString()}`,
        });
      } else {
        // Regular users get tickets by referTo
        const params = new URLSearchParams();
        if (filters.keyword && filters.keyword.trim()) params.append('keyword', filters.keyword.trim());
        if (filters.fromDate) params.append('fromDate', filters.fromDate);
        if (filters.toDate) params.append('toDate', filters.toDate);
        if (filters.status && filters.status !== 'all') params.append('status', filters.status);

        const queryString = params.toString();
        response = await request({
          method: 'get',
          url: `/tickets/${referTo}${queryString ? `?${queryString}` : ''}`,
        });
      }

      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  // Fetch companies for Tech user ticket creation
  const fetchCompanies = async () => {
    if (!isTechUser) return;
    setLoadingCompanies(true);
    try {
      const response = await request({
        method: 'get',
        url: '/users/companies-for-tech',
      });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
    setLoadingCompanies(false);
  };

  const handleSearch = () => {
    fetchTickets({
      fromDate: filterDateFrom,
      toDate: filterDateTo,
      status: filterStatus,
      groupname: filterGroupname,
      keyword: filterKeyword,
    });
  };

  // Get unique groupnames for filter dropdown
  const uniqueGroupnames = useMemo(() => {
    const names = tickets
      .map((t) => t.groupname)
      .filter((name) => name && name.trim() !== '');
    return [...new Set(names)].sort();
  }, [tickets]);

  // Filter tickets - Tech users filter on backend, others filter locally
  // Priority order for sorting
  const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };

  const filteredTickets = useMemo(() => {
    let result = [...tickets];

    // Non-Tech users: apply backend filter states locally
    if (!isTechUser) {
      result = result.filter((ticket) => {
        // Filter by status
        if (filterStatus !== 'all' && ticket.status !== filterStatus) {
          return false;
        }
        // Filter by date range
        if (filterDateFrom) {
          const ticketDate = new Date(ticket.createdAt);
          const fromDate = new Date(filterDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (ticketDate < fromDate) return false;
        }
        if (filterDateTo) {
          const ticketDate = new Date(ticket.createdAt);
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          if (ticketDate > toDate) return false;
        }
        return true;
      });
    }

    // Apply local filters (for all users)
    // Filter by category
    if (localCategoryFilter !== 'all') {
      result = result.filter((ticket) => ticket.category === localCategoryFilter);
    }

    // Filter by status (local)
    if (localStatusFilter !== 'all') {
      result = result.filter((ticket) => ticket.status === localStatusFilter);
    }

    // Filter by keyword in description (local)
    if (localKeyword.trim()) {
      const kw = localKeyword.trim().toLowerCase();
      result = result.filter((ticket) => ticket.description?.toLowerCase().includes(kw));
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'priority-high':
          return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
        case 'newest':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    return result;
  }, [tickets, isTechUser, filterStatus, filterDateFrom, filterDateTo, localCategoryFilter, localStatusFilter, localKeyword, sortBy]);

  const clearFilters = () => {
    const defaultFromDate = isTechUser ? getDefault30DaysAgo() : '';
    const defaultToDate = isTechUser ? getToday() : '';
    const defaultStatus = isTechUser ? 'open' : 'all';
    setFilterGroupname('');
    setFilterStatus(defaultStatus);
    setFilterDateFrom(defaultFromDate);
    setFilterDateTo(defaultToDate);
    setFilterKeyword('');

    // Reset local filters
    setLocalCategoryFilter('all');
    setLocalStatusFilter('all');
    setLocalKeyword('');
    setSortBy('newest');

    // Re-fetch with cleared filters
    if (isTechUser) {
      fetchTickets({
        fromDate: defaultFromDate,
        toDate: defaultToDate,
        status: 'open',  // Default to open tickets
        groupname: '',
      });
    } else {
      fetchTickets(); // Fetch all tickets without filters
    }
  };

  const handleCreateTicket = async () => {
    if (!formData.title || !formData.description) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please fill in title and description',
      });
      return;
    }

    // Tech users must select a company
    if (isTechUser && !selectedCompany) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please select a company',
      });
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append('category', formData.category);
      data.append('type', formData.type);
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('machineId', formData.machineId);

      // For Tech users, use selected company info
      if (isTechUser && selectedCompany) {
        data.append('referTo', selectedCompany.email);
        data.append('groupname', selectedCompany.groupname || '');
        data.append('groupCode', selectedCompany.groupCode || '');
        data.append('internal', isInternal);
        data.append('priority', priority);
      } else {
        data.append('referTo', referTo);
        data.append('groupname', user?.groupname || '');
        data.append('groupCode', user?.groupCode || '');
      }

      images.forEach((img) => {
        data.append('images', img);
      });
      videos.forEach((vid) => {
        data.append('videos', vid);
      });

      await request({
        method: 'post',
        url: '/tickets/create',
        data: data,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          let percent = Math.floor((loaded * 100) / total);
          setUploadPercent(percent);
        },
      });

      setUploadPercent(100);
      setTimeout(() => {
        setUploadPercent(0);
      }, 1000);

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Ticket created successfully',
      });

      setOpenCreateDialog(false);
      resetForm();
      // Refresh tickets with current filters for Tech users
      if (isTechUser) {
        fetchTickets({
          fromDate: filterDateFrom,
          toDate: filterDateTo,
          status: filterStatus,
          groupname: filterGroupname,
        });
      } else {
        fetchTickets();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create ticket',
      });
    }
    setLoading(false);
  };

  const handleAddReply = async () => {
    if (!replyMessage.trim()) {
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append('message', replyMessage);
      data.append('referTo', referTo);

      replyFiles.forEach((file) => {
        if (file.type.startsWith('image/')) {
          data.append('images', file);
        } else {
          data.append('videos', file);
        }
      });

      const response = await request({
        method: 'post',
        url: `/tickets/${selectedTicket._id}/reply`,
        data: data,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          let percent = Math.floor((loaded * 100) / total);
          setUploadPercent(percent);
        },
      });

      setUploadPercent(100);
      setTimeout(() => {
        setUploadPercent(0);
      }, 500);

      setSelectedTicket(response.data);
      setReplyMessage('');
      setReplyFiles([]);
      fetchTickets();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add reply',
      });
    }
    setLoading(false);
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    setStatusLoading(true);
    try {
      const response = await request({
        method: 'patch',
        url: `/tickets/${ticketId}/status`,
        data: { status: newStatus },
      });
      setSelectedTicket(response.data);
      fetchTickets();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update status',
      });
    }
    setStatusLoading(false);
  };

  const handleUpdatePriority = async (ticketId, newPriority) => {
    setPriorityLoading(true);
    try {
      const response = await request({
        method: 'patch',
        url: `/tickets/${ticketId}/priority`,
        data: { priority: newPriority },
      });
      setSelectedTicket(response.data);
      // Refresh tickets with current filters
      if (isTechUser) {
        fetchTickets({
          fromDate: filterDateFrom,
          toDate: filterDateTo,
          status: filterStatus,
          groupname: filterGroupname,
        });
      } else {
        fetchTickets();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update priority',
      });
    }
    setPriorityLoading(false);
  };

  const resetForm = () => {
    setFormData({
      category: 'Software',
      type: 'Issue',
      title: '',
      description: '',
      machineId: '',
    });
    setImages([]);
    setVideos([]);
    // Reset Tech-specific states
    setSelectedCompany(null);
    setIsInternal(false);
    setPriority('medium');
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages((prev) => [...prev, ...files]);
  };

  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    setVideos((prev) => [...prev, ...files]);
  };

  const handleReplyFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setReplyFiles((prev) => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeVideo = (index) => {
    setVideos((prev) => prev.filter((_, i) => i !== index));
  };

  const removeReplyFile = (index) => {
    setReplyFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const openTicketDetail = async (ticket) => {
    try {
      const response = await request({
        method: 'get',
        url: `/ticket/${ticket._id}`,
      });
      setSelectedTicket(response.data);
      setOpenViewDialog(true);
    } catch (error) {
      console.error('Error fetching ticket detail:', error);
    }
  };

  const getMediaUrl = (filename, type) => {
    const endpoint = type === 'image' ? 'ticket-image' : 'ticket-video';
    return `${process.env.REACT_APP_SERVER_API}/${endpoint}/${filename}`;
  };

  const openMediaPreview = (url, type) => {
    setMediaPreview({ open: true, url, type });
  };

  const closeMediaPreview = () => {
    setMediaPreview({ open: false, url: '', type: '' });
  };

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Tickets
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            if (isTechUser) {
              fetchCompanies();
            }
            setOpenCreateDialog(true);
          }}
        >
          New Ticket
        </Button>
      </Box>

      {/* Filter Section */}
      <Paper className={classes.paper} sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {isTechUser && (
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                id="filter-groupname"
                size="small"
                options={[{ displayName: 'All Groups', groupname: '' }, ...companies]}
                getOptionLabel={(option) => option.displayName || option}
                value={companies.find((c) => c.groupname === filterGroupname) || { displayName: 'All Groups', groupname: '' }}
                onChange={(event, newValue) => {
                  setFilterGroupname(newValue?.groupname || '');
                }}
                isOptionEqualToValue={(option, value) => option.groupname === value.groupname}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Group Name"
                  />
                )}
                disableClearable
                loading={loadingCompanies}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Keyword"
                placeholder="Search description..."
                value={filterKeyword}
                onChange={(e) => setFilterKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button variant="contained" onClick={handleSearch} fullWidth>
                Search
              </Button>
            </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button variant="outlined" onClick={clearFilters} fullWidth>
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Local Filter & Sort Section */}
      {tickets.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
            Quick Filter:
          </Typography>
          {/* Category Filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={localCategoryFilter}
              onChange={(e) => setLocalCategoryFilter(e.target.value)}
              label="Category"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="Software">Software</MenuItem>
              <MenuItem value="Hardware">Hardware</MenuItem>
            </Select>
          </FormControl>
          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={localStatusFilter}
              onChange={(e) => setLocalStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          {/* Keyword Filter */}
          <TextField
            size="small"
            label="Keyword"
            placeholder="Search description..."
            value={localKeyword}
            onChange={(e) => setLocalKeyword(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          {/* Sort */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Sort By"
            >
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
              {isTechUser && <MenuItem value="priority-high">Priority (High → Low)</MenuItem>}
            </Select>
          </FormControl>
          {/* Result count */}
          <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
            {filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* Ticket List */}
      {filteredTickets.length === 0 ? (
        <Paper className={classes.paper} sx={{ textAlign: 'center', py: 5 }}>
          <Typography color="textSecondary">
            {tickets.length === 0
              ? isTechUser
                ? 'No tickets matching your responsibilities.'
                : 'No tickets yet. Submit your first ticket!'
              : 'No tickets match the current filters.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredTickets.map((ticket) => (
            <Grid item xs={12} md={6} key={ticket._id}>
              <Card className={classes.ticketCard} onClick={() => openTicketDetail(ticket)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Chip
                        label={ticket.category}
                        size="small"
                        color={ticket.category === 'Software' ? 'primary' : 'secondary'}
                        className={classes.categoryChip}
                      />
                      <Chip label={ticket.type} size="small" variant="outlined" />
                    </Box>
                    <Chip
                      label={ticket.status}
                      size="small"
                      className={
                        ticket.status === 'open' ? classes.statusOpen : classes.statusClosed
                      }
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {ticket.groupname && (
                      <Chip
                        label={ticket.groupname}
                        size="small"
                        variant="outlined"
                        color="info"
                      />
                    )}
                    {ticket.internal && (
                      <Chip
                        label="Internal"
                        size="small"
                        color="warning"
                      />
                    )}
                    {/* Priority - only visible to Tech users */}
                    {isTechUser && ticket.priority && (
                      <Chip
                        label={ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                        size="small"
                        sx={{
                          backgroundColor: priorityOptions.find((p) => p.value === ticket.priority)?.color || '#2196f3',
                          color: 'white',
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Box sx={{ bgcolor: '#1976d2', color: 'white', fontSize: '0.65rem', fontWeight: 700, px: 0.7, py: 0.2, borderRadius: 0.5, lineHeight: 1.4 }}>
                      ID
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                      {ticket.ticketNumber != null ? String(ticket.ticketNumber).padStart(6, '0') : '------'}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {ticket.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" noWrap>
                    {ticket.description}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      By: {ticket.createdBy?.firstname} {ticket.createdBy?.lastname}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color={ticket.replies?.length > 0 ? 'primary' : 'textSecondary'}>
                    {ticket.replies?.length || 0} {ticket.replies?.length === 1 ? 'reply' : 'replies'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Ticket Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          New Ticket
          <IconButton
            onClick={() => setOpenCreateDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <FormLabel>Category</FormLabel>
                <RadioGroup
                  row
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <FormControlLabel value="Software" control={<Radio />} label="Software" />
                  <FormControlLabel value="Hardware" control={<Radio />} label="Hardware" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <FormLabel>Type</FormLabel>
                <RadioGroup
                  row
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <FormControlLabel value="Issue" control={<Radio />} label="Issue" />
                  <FormControlLabel value="Question" control={<Radio />} label="Question" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {/* Tech user: Company Selection */}
            {isTechUser && (
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="company-select"
                  options={companies}
                  getOptionLabel={(option) => option.displayName || ''}
                  value={selectedCompany}
                  onChange={(event, newValue) => {
                    setSelectedCompany(newValue);
                  }}
                  loading={loadingCompanies}
                  isOptionEqualToValue={(option, value) => option.email === value?.email}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Company"
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCompanies ? 'Loading...' : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            {/* Tech user: Internal Checkbox */}
            {isTechUser && (
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                    />
                  }
                  label="Internal"
                />
              </Grid>
            )}
            {/* Tech user: Priority Selection */}
            {isTechUser && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    label="Priority"
                  >
                    {priorityOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: opt.color,
                            }}
                          />
                          {opt.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Machine ID (Optional)"
                value={formData.machineId}
                onChange={(e) => setFormData({ ...formData, machineId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" component="label" startIcon={<ImageIcon />}>
                  Upload Images
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                  />
                </Button>
                <Button variant="outlined" component="label" startIcon={<VideoLibraryIcon />}>
                  Upload Videos
                  <input
                    type="file"
                    hidden
                    accept="video/mp4,video/quicktime"
                    multiple
                    onChange={handleVideoUpload}
                  />
                </Button>
              </Box>
              <div className={classes.filePreview}>
                {images.map((img, index) => (
                  <div key={index} className={classes.previewItem}>
                    <img
                      src={URL.createObjectURL(img)}
                      alt="preview"
                      className={classes.previewImage}
                    />
                    <IconButton
                      size="small"
                      className={classes.removeButton}
                      onClick={() => removeImage(index)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
                {videos.map((vid, index) => (
                  <div key={index} className={classes.previewItem}>
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#f0f0f0',
                      }}
                    >
                      <VideoLibraryIcon />
                    </Box>
                    <IconButton
                      size="small"
                      className={classes.removeButton}
                      onClick={() => removeVideo(index)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
            </Grid>
            {uploadPercent > 0 && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%', mt: 2 }}>
                  <LinearProgress variant="determinate" value={uploadPercent} />
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                    Uploading... {uploadPercent}%
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)} disabled={loading}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTicket} disabled={loading || uploadPercent > 0}>
            {uploadPercent > 0 ? 'Uploading...' : 'New Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Ticket Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Chip
                    label={selectedTicket.category}
                    size="small"
                    color={selectedTicket.category === 'Software' ? 'primary' : 'secondary'}
                    sx={{ mr: 1 }}
                  />
                  <Chip label={selectedTicket.type} size="small" variant="outlined" sx={{ mr: 1 }} />
                  <Chip
                    label={selectedTicket.status}
                    size="small"
                    className={
                      selectedTicket.status === 'open' ? classes.statusOpen : classes.statusClosed
                    }
                  />
                  {selectedTicket.groupname && (
                    <Chip
                      label={selectedTicket.groupname}
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{ ml: 1 }}
                    />
                  )}
                  {selectedTicket.internal && (
                    <Chip
                      label="Internal"
                      size="small"
                      color="warning"
                      sx={{ ml: 1 }}
                    />
                  )}
                  {/* Priority selector - only for Tech users */}
                  {isTechUser && (
                    <FormControl size="small" sx={{ ml: 1, minWidth: 100 }}>
                      <Select
                        value={selectedTicket.priority || 'medium'}
                        onChange={(e) => handleUpdatePriority(selectedTicket._id, e.target.value)}
                        disabled={priorityLoading}
                        sx={{ height: 32 }}
                      >
                        {priorityOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  backgroundColor: opt.color,
                                }}
                              />
                              {opt.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
                <Box>
                  {selectedTicket.status === 'open' ? (
                    <Button
                      size="small"
                      onClick={() => handleUpdateStatus(selectedTicket._id, 'closed')}
                      disabled={statusLoading}
                      startIcon={statusLoading ? <CircularProgress size={16} /> : null}
                    >
                      {statusLoading ? 'Closing...' : 'Close Ticket'}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => handleUpdateStatus(selectedTicket._id, 'open')}
                      disabled={statusLoading}
                      startIcon={statusLoading ? <CircularProgress size={16} /> : null}
                    >
                      {statusLoading ? 'Reopening...' : 'Reopen Ticket'}
                    </Button>
                  )}
                  <IconButton onClick={() => setOpenViewDialog(false)} disabled={statusLoading}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Box sx={{ bgcolor: '#1976d2', color: 'white', fontSize: '0.65rem', fontWeight: 700, px: 0.7, py: 0.2, borderRadius: 0.5, lineHeight: 1.4 }}>
                  ID
                </Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                  {selectedTicket.ticketNumber != null ? String(selectedTicket.ticketNumber).padStart(6, '0') : '------'}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {selectedTicket.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                By: {selectedTicket.createdBy?.firstname} {selectedTicket.createdBy?.lastname} |{' '}
                {new Date(selectedTicket.createdAt).toLocaleString()}
                {selectedTicket.machineId && ` | Machine: ${selectedTicket.machineId}`}
              </Typography>
              {/* Show who closed the ticket */}
              {selectedTicket.status === 'closed' && selectedTicket.closedBy && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  Closed by: {selectedTicket.closedBy?.firstname} {selectedTicket.closedBy?.lastname} |{' '}
                  {selectedTicket.closedAt && new Date(selectedTicket.closedAt).toLocaleString()}
                </Typography>
              )}
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedTicket.description}
              </Typography>

              {/* Ticket Attachments */}
              {(selectedTicket.images?.length > 0 || selectedTicket.videos?.length > 0) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Attachments:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedTicket.images?.map((img, idx) => (
                      <img
                        key={idx}
                        src={getMediaUrl(img, 'image')}
                        alt="attachment"
                        style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                        onClick={() => openMediaPreview(getMediaUrl(img, 'image'), 'image')}
                      />
                    ))}
                    {selectedTicket.videos?.map((vid, idx) => (
                      <video
                        key={idx}
                        src={getMediaUrl(vid, 'video')}
                        style={{ width: 150, height: 100, borderRadius: 8, cursor: 'pointer' }}
                        onClick={() => openMediaPreview(getMediaUrl(vid, 'video'), 'video')}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Replies */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Replies ({selectedTicket.replies?.length || 0})
              </Typography>
              <List>
                {selectedTicket.replies?.map((reply, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                    <Box className={classes.replyBox} sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                          {reply.user?.firstname?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {reply.user?.firstname} {reply.user?.lastname}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(reply.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {reply.message}
                      </Typography>
                      {reply.attachments?.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {reply.attachments.map((att, idx) => {
                            const isVideo = /\.(mp4|mov|quicktime)$/i.test(att);
                            if (!isVideo) {
                              return (
                                <img
                                  key={idx}
                                  src={getMediaUrl(att, 'image')}
                                  alt="attachment"
                                  style={{
                                    width: 80,
                                    height: 80,
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => openMediaPreview(getMediaUrl(att, 'image'), 'image')}
                                />
                              );
                            }
                            return (
                              <video
                                key={idx}
                                src={getMediaUrl(att, 'video')}
                                style={{ width: 120, height: 80, borderRadius: 8, cursor: 'pointer' }}
                                onClick={() => openMediaPreview(getMediaUrl(att, 'video'), 'video')}
                              />
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>

              {/* Reply Input */}
              {selectedTicket.status === 'open' && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Write a reply..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    multiline
                    rows={2}
                    sx={{ mb: 1 }}
                    disabled={loading}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Button
                        size="small"
                        component="label"
                        startIcon={<AttachFileIcon />}
                        disabled={loading}
                      >
                        Attach
                        <input
                          type="file"
                          hidden
                          accept="image/*,video/mp4,video/quicktime"
                          multiple
                          onChange={handleReplyFileUpload}
                          disabled={loading}
                        />
                      </Button>
                      {replyFiles.length > 0 && (
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          {replyFiles.length} file(s) selected
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      endIcon={<SendIcon />}
                      onClick={handleAddReply}
                      disabled={loading || !replyMessage.trim() || uploadPercent > 0}
                    >
                      {uploadPercent > 0 ? 'Uploading...' : 'Reply'}
                    </Button>
                  </Box>
                  {uploadPercent > 0 && (
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <LinearProgress variant="determinate" value={uploadPercent} />
                      <Typography variant="caption" color="textSecondary" align="center" sx={{ display: 'block', mt: 0.5 }}>
                        Uploading... {uploadPercent}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Media Preview Dialog */}
      <Dialog
        open={mediaPreview.open}
        onClose={closeMediaPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Preview</Typography>
            <IconButton onClick={closeMediaPreview}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
          {mediaPreview.type === 'image' ? (
            <img
              src={mediaPreview.url}
              alt="preview"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          ) : (
            <video
              src={mediaPreview.url}
              controls
              autoPlay
              style={{ maxWidth: '100%', maxHeight: '70vh' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Full-screen Loading Overlay */}
      {(loading || statusLoading) && <LoadingIcon />}
    </Container>
  );
}
